import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import ProductPicker, { CategoryPicker } from '../../components/admin/ProductPicker';
import {
  API, withCreds, inputCls, selectCls, btnPrimary, btnSecondary, Field, Modal, PageHeader, Spinner,
  ScheduleBadge, discountLabel, fmtDateTime, toLocalInput, fromLocalInput, errMsg,
} from '../../components/admin/AdminUI';
import { Plus, Edit, Trash2, TrendingDown, Megaphone } from 'lucide-react';
import Select from '../../components/FlatSelect';

const plusDays = (d) => new Date(Date.now() + d * 86400000).toISOString();

function SaleModal({ sale, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', description: '', discount_type: 'percent', discount_value: 10,
    start_date: new Date().toISOString(), end_date: plusDays(7), applies_to: 'all', product_ids: [], categories: [], active: true,
    ...(sale || {}), ...(sale?.applies_to === 'specific' ? { applies_to: 'products' } : {}),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    const { id, status, created_at, ...body } = form;
    try {
      if (sale) await axios.put(`${API}/admin/sales/${sale.id}`, body, withCreds);
      else await axios.post(`${API}/admin/sales`, body, withCreds);
      onSaved();
    } catch (err) { setError(errMsg(err, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={sale ? 'Edit Sale' : 'New Sale'} onClose={onClose} width="max-w-2xl">
      <form onSubmit={save} className="space-y-4">
        <Field label="Sale name *" hint="Shown to shoppers on sale items, e.g. 'Spring Sale'">
          <input value={form.name} onChange={e => set('name', e.target.value)} required data-testid="sale-name-input" className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Discount">
            <div className="flex gap-2">
              <Select value={form.discount_type} onChange={e => set('discount_type', e.target.value)} className={selectCls}>
                <option value="percent">% off</option><option value="fixed">$ off each</option>
              </Select>
              <input type="number" step="0.01" min="0" value={form.discount_value} onChange={e => set('discount_value', parseFloat(e.target.value) || 0)} required className={inputCls} />
            </div>
          </Field>
          <label className="flex items-center gap-2 cursor-pointer self-end pb-3">
            <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="w-4 h-4 accent-honey" />
            <span className="text-ink/75 text-sm">Enabled</span>
          </label>
          <Field label="Starts *">
            <input type="datetime-local" value={toLocalInput(form.start_date)} onChange={e => set('start_date', fromLocalInput(e.target.value))} required className={inputCls} style={{ colorScheme: 'light' }} />
          </Field>
          <Field label="Ends *">
            <input type="datetime-local" value={toLocalInput(form.end_date)} onChange={e => set('end_date', fromLocalInput(e.target.value))} required className={inputCls} style={{ colorScheme: 'light' }} />
          </Field>
        </div>
        <Field label="Applies to">
          <div className="flex gap-2 mb-3">
            {[['all', 'Everything'], ['categories', 'Categories'], ['products', 'Specific items']].map(([v, l]) => (
              <button type="button" key={v} onClick={() => set('applies_to', v)}
                className={`px-4 py-2 rounded-xl border text-sm ${form.applies_to === v ? 'bg-honey/10 border-honey/40 text-honey-700' : 'bg-ink/[0.04] border-ink/10 text-ink/70'}`}>{l}</button>
            ))}
          </div>
          {form.applies_to === 'categories' && <CategoryPicker value={form.categories || []} onChange={v => set('categories', v)} />}
          {form.applies_to === 'products' && <ProductPicker value={form.product_ids || []} onChange={v => set('product_ids', v)} />}
        </Field>
        <p className="text-ink/45 text-xs">If several sales cover the same item, shoppers get whichever gives the lowest price. Coupons apply on top of sale prices unless a coupon excludes sale items.</p>
        {error && <p className="text-red-600 text-sm p-3 bg-red-500/10 rounded-xl">{error}</p>}
        <div className="flex gap-3">
          <button type="submit" disabled={saving} data-testid="save-sale-btn" className={`${btnPrimary} flex-1`}>{saving ? 'Saving…' : 'Save sale'}</button>
          <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

export default function AdminSales() {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { setSales((await axios.get(`${API}/admin/sales`, withCreds)).data.sales); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Delete the sale "${s.name}"?`)) return;
    await axios.delete(`${API}/admin/sales/${s.id}`, withCreds);
    load();
  };

  const scopeText = (s) => (s.applies_to === 'categories' ? (s.categories || []).join(', ') || 'No categories picked'
    : s.applies_to === 'products' ? `${(s.product_ids || []).length} item(s)` : 'Everything');

  return (
    <AdminLayout>
      {editing && <SaleModal sale={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <PageHeader title="Sales" subtitle="Timed discounts that change prices across the store automatically">
        <button onClick={() => setEditing('new')} data-testid="add-sale-btn" className={btnPrimary}><Plus className="w-4 h-4" /> New Sale</button>
      </PageHeader>
      <div className="admin-card rounded-xl overflow-x-auto">
        {loading ? <Spinner /> : sales.length === 0 ? (
          <div className="text-center py-12"><TrendingDown className="w-8 h-8 text-ink/30 mx-auto mb-2" /><p className="text-ink/45 text-sm">No sales yet.</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-ink/10">
              <tr className="text-ink/55 text-xs uppercase tracking-widest">
                <th className="text-left p-3">Sale</th><th className="text-left p-3">Discount</th><th className="text-left p-3">Applies to</th>
                <th className="text-left p-3">When</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/10">
              {sales.map(s => (
                <tr key={s.id} className="text-ink/75 hover:bg-ink/[0.04]">
                  <td className="p-3 text-ink font-medium">{s.name}</td>
                  <td className="p-3 text-honey-600 font-bold">{discountLabel(s.discount_type, s.discount_value)}</td>
                  <td className="p-3 text-xs">{scopeText(s)}</td>
                  <td className="p-3 text-xs text-ink/60">{fmtDateTime(s.start_date)}<br />→ {fmtDateTime(s.end_date)}</td>
                  <td className="p-3"><ScheduleBadge status={s.status} /></td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button title="Advertise this sale" onClick={() => navigate(`/admin/promotions?sale=${s.id}`)} className="text-ink/55 hover:text-honey-600"><Megaphone className="w-4 h-4" /></button>
                      <button title="Edit" onClick={() => setEditing(s)} className="text-ink/55 hover:text-honey-700"><Edit className="w-4 h-4" /></button>
                      <button title="Delete" onClick={() => remove(s)} className="text-ink/55 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
